import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    purchaseCreditsService,
    listSavedCardsService,
    setupCardService,
    setPrimaryCardService,
} from '../../../services/billing.service';

const formatCardBrand = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

const pickPrimaryCard = (cards) => {
    if (!cards?.length) return null;
    return cards.find((c) => c.is_default) || cards[0];
};

// ── Internal payment form ──────────────────────────────────────────────────
const CreditTopUpForm = ({ amount, dqInsiderInfo, onPaymentSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [internalClientSecret, setInternalClientSecret] = useState(null);
    const [savedCards, setSavedCards] = useState([]);
    const [cardsLoading, setCardsLoading] = useState(true);
    const [useManualCard, setUseManualCard] = useState(false);
    const [saveCardForFuture, setSaveCardForFuture] = useState(true);
    const [cardName, setCardName] = useState(
        dqInsiderInfo?.profile?.business_name ||
        (dqInsiderInfo?.profile?.first_name
            ? `${dqInsiderInfo.profile.first_name} ${dqInsiderInfo.profile.last_name}`
            : '')
    );

    const initiatingRef = useRef(false);
    const confirmingRef = useRef(false);

    // The card to pay with: the one the buyer picked, else the primary card.
    const [selectedCardId, setSelectedCardId] = useState(null);

    const primaryCard = pickPrimaryCard(savedCards);
    const selectedCard = savedCards.find((c) => c.payment_method_id === selectedCardId) || primaryCard;
    // Primary first, then the rest as the server lists them.
    const orderedCards = primaryCard
        ? [primaryCard, ...savedCards.filter((c) => c.payment_method_id !== primaryCard.payment_method_id)]
        : [];
    const showSavedCardFlow = Boolean(primaryCard) && !useManualCard;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCardsLoading(true);
            try {
                const res = await listSavedCardsService();
                if (!cancelled && res?.data?.payment_methods) {
                    setSavedCards(res.data.payment_methods);
                }
            } catch (_) {
                if (!cancelled) setSavedCards([]);
            } finally {
                if (!cancelled) setCardsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleInitiatePurchase = async () => {
        if (initiatingRef.current || processing) return;
        initiatingRef.current = true;
        setProcessing(true);
        setError(null);
        try {
            const response = await purchaseCreditsService(amount);
            if (response?.data?.client_secret) {
                setInternalClientSecret(response.data.client_secret);
            }
        } catch (err) {
            setError(err.message || 'Failed to initiate purchase');
        } finally {
            initiatingRef.current = false;
            setProcessing(false);
        }
    };

    const handlePayWithSavedCard = async () => {
        if (!stripe || !selectedCard || confirmingRef.current || processing) return;
        confirmingRef.current = true;
        setProcessing(true);
        setError(null);
        try {
            const response = await purchaseCreditsService(amount);
            const secret = response?.data?.client_secret;
            if (!secret) {
                setError('Failed to initiate purchase');
                return;
            }
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(secret, {
                payment_method: selectedCard.payment_method_id,
            });
            if (stripeError) {
                setError(stripeError.message);
            } else if (paymentIntent?.status === 'succeeded') {
                onPaymentSuccess();
            }
        } catch (err) {
            setError(err.message || 'Payment failed');
        } finally {
            confirmingRef.current = false;
            setProcessing(false);
        }
    };

    const saveCardUsingExistingSetupApi = async () => {
        const cardElement = elements?.getElement(CardElement);
        if (!stripe || !cardElement) return;

        const setupRes = await setupCardService();
        const setupSecret = setupRes?.data?.client_secret;
        if (!setupSecret) return;

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(setupSecret, {
            payment_method: {
                card: cardElement,
                billing_details: { name: cardName || 'DQ Insider' },
            },
        });
        if (setupError || setupIntent?.status !== 'succeeded') return;

        const pmId = typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method?.id;
        if (pmId) {
            await setPrimaryCardService(pmId);
        }
    };

    const handleConfirmPayment = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !internalClientSecret) return;
        if (confirmingRef.current || processing) return;
        confirmingRef.current = true;
        setProcessing(true);
        setError(null);

        try {
            const cardElement = elements.getElement(CardElement);
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(internalClientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: { name: cardName || 'DQ Insider' },
                },
            });

            if (stripeError) {
                setError(stripeError.message);
            } else if (paymentIntent?.status === 'succeeded') {
                if (saveCardForFuture) {
                    try {
                        await saveCardUsingExistingSetupApi();
                    } catch (_) {
                        // Payment succeeded; do not block credits if save-card fails
                    }
                }
                onPaymentSuccess();
            }
        } catch (err) {
            setError(err.message || 'Payment confirmation failed');
        } finally {
            confirmingRef.current = false;
            setProcessing(false);
        }
    };

    const summaryBox = (
        <Box sx={{
            mb: 3, p: 3, width: '100%',
            // Low-alpha tint over the page background instead of a solid pastel hex —
            // reads correctly against both light and dark surfaces, since the global
            // dark-mode override always force-flips Typography text to var(--brand-text).
            backgroundColor: 'rgba(254,90,90,0.06)', borderRadius: '12px',
            border: '1px solid rgba(254,90,90,0.20)', boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" sx={{ color: 'var(--brand-text-muted, #64748b)', fontWeight: 500 }}>Credits to purchase:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--brand-text, #1e293b)' }}>{amount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px dashed rgba(254,90,90,0.35)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--brand-text, #1e293b)' }}>Total Cost:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#FF6900' }}>
                    ${amount}.00{' '}
                    <Typography component="span" variant="caption" sx={{ color: 'var(--brand-text-muted, #94a3b8)' }}>USD</Typography>
                </Typography>
            </Box>
        </Box>
    );

    const errorBox = error && (
        <Box sx={{ mb: 3, p: 1.5, width: '100%', backgroundColor: 'rgba(254,90,90,0.06)', border: '1px solid rgba(254,90,90,0.20)', borderRadius: '8px' }}>
            <Typography color="error" sx={{ fontSize: '0.875rem', fontWeight: 500, textAlign: 'center' }}>{error}</Typography>
        </Box>
    );

    const payButtonSx = {
        backgroundColor: '#FF6900', color: '#fff',
        px: 6, py: 1.5, fontWeight: 700, borderRadius: '10px',
        textTransform: 'none', fontSize: '1rem', minWidth: '240px',
        boxShadow: '0 4px 12px rgba(255,100,104,0.25)',
        '&:hover': { backgroundColor: '#e55e00', boxShadow: '0 6px 16px rgba(255,100,104,0.35)' },
    };

    const useDifferentCardLink = showSavedCardFlow && (
        <Button
            variant="text"
            size="small"
            onClick={() => {
                setUseManualCard(true);
                setError(null);
                setInternalClientSecret(null);
                setSaveCardForFuture(true);
            }}
            sx={{ mt: 1.5, textTransform: 'none', color: '#64748b', fontWeight: 600, '&:hover': { color: '#FF6900', backgroundColor: 'transparent' } }}
        >
            Use a different card
        </Button>
    );

    const backToSavedLink = primaryCard && useManualCard && !internalClientSecret && (
        <Button
            variant="text"
            size="small"
            onClick={() => {
                setUseManualCard(false);
                setError(null);
                setInternalClientSecret(null);
            }}
            sx={{ mt: 1.5, textTransform: 'none', color: '#64748b', fontWeight: 600, '&:hover': { color: '#FF6900', backgroundColor: 'transparent' } }}
        >
            Pay with saved card instead
        </Button>
    );

    // Every saved card, primary first and preselected; one click picks another.
    const savedCardBox = primaryCard && (
        <Box role="radiogroup" aria-label="Saved cards" sx={{ mb: 3, width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {orderedCards.map((card) => {
                const selected = card.payment_method_id === selectedCard?.payment_method_id;
                const select = () => { setSelectedCardId(card.payment_method_id); setError(null); };
                return (
                    <Box
                        key={card.payment_method_id}
                        role="radio" aria-checked={selected} tabIndex={0}
                        onClick={select}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}
                        sx={{
                            p: 2, display: 'flex', alignItems: 'center', gap: 1.75, cursor: 'pointer',
                            border: `1.5px solid ${selected ? '#FF6900' : '#e2e8f0'}`, borderRadius: '12px',
                            backgroundColor: selected ? '#fff5f4' : '#fff',
                            '&:focus-visible': { outline: '2px solid #FF6900', outlineOffset: 2 },
                        }}
                    >
                        <Box sx={{
                            width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                            border: `2px solid ${selected ? '#FF6900' : '#94a3b8'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {selected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FF6900' }}/>}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem' }}>
                                {formatCardBrand(card.brand)} •••• {card.last4}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                            </Typography>
                        </Box>
                        {card.is_default && (
                            <Typography variant="caption" sx={{ color: '#FF6900', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                Primary
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );

    // Manual card entry (after Proceed to Checkout)
    if (internalClientSecret) {
        return (
            <form onSubmit={handleConfirmPayment} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                {summaryBox}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3, mt: 1, color: '#1e293b', width: '100%', textAlign: 'left' }}>
                    Secure Card Payment
                </Typography>
                <Box sx={{ mb: 3, width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#475569' }}>Cardholder Name</Typography>
                    <TextField
                        variant="outlined" size="small" fullWidth
                        placeholder="Name on card"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        required
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px', backgroundColor: '#fff',
                                '&:hover fieldset': { borderColor: '#FF6900' },
                                '&.Mui-focused fieldset': { borderColor: '#FF6900' },
                            },
                        }}
                    />
                </Box>
                <Box sx={{ mb: 3, width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#475569' }}>Card Details</Typography>
                    <Box sx={{
                        p: 2, border: '1px solid #e2e8f0', borderRadius: '10px',
                        backgroundColor: '#fff', transition: 'all 0.2s',
                        '&:focus-within': { borderColor: '#FF6900', boxShadow: '0 0 0 3px rgba(255,100,104,0.1)' },
                    }}>
                        <CardElement options={{ style: { base: { fontSize: '16px', color: '#1e293b', fontFamily: "'Inter', sans-serif" } } }} />
                    </Box>
                </Box>
                <FormControlLabel
                    sx={{ width: '100%', mb: 2, ml: 0, alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={saveCardForFuture}
                            onChange={(e) => setSaveCardForFuture(e.target.checked)}
                            sx={{
                                color: '#94a3b8',
                                '&.Mui-checked': { color: '#FF6900' },
                                pt: 0.25,
                            }}
                        />
                    }
                    label={
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>
                                Save this card for future payments
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mt: 0.25 }}>
                                Uses the same Add Card flow (setup + default card).
                            </Typography>
                        </Box>
                    }
                />
                {errorBox}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!stripe || processing}
                    sx={payButtonSx}
                >
                    {processing ? 'Processing Payment...' : `Pay $${amount}.00 USD`}
                </Button>
                {backToSavedLink}
            </form>
        );
    }

    // Step 1: Checkout summary — saved card one-click or manual proceed
    return (
        <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {summaryBox}
            {cardsLoading ? (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem', mb: 2 }}>Loading payment methods...</Typography>
            ) : showSavedCardFlow ? (
                <>
                    {savedCardBox}
                    {errorBox}
                    <Button
                        variant="contained"
                        onClick={handlePayWithSavedCard}
                        disabled={!stripe || processing}
                        sx={payButtonSx}
                    >
                        {processing
                            ? 'Processing Payment...'
                            : `Pay $${amount}.00 with ${formatCardBrand(selectedCard.brand)} •••• ${selectedCard.last4}`}
                    </Button>
                    {useDifferentCardLink}
                </>
            ) : (
                <>
                    {errorBox}
                    <Button
                        variant="contained"
                        onClick={handleInitiatePurchase}
                        disabled={processing}
                        sx={payButtonSx}
                    >
                        Proceed to Checkout
                    </Button>
                </>
            )}
            {!cardsLoading && !showSavedCardFlow && primaryCard && useManualCard && backToSavedLink}
        </Box>
    );
};

// ── Dialog wrapper ─────────────────────────────────────────────────────────
const TopUpCreditsDialog = ({
    open,
    onClose,
    topUpAmount,
    setTopUpAmount,
    topUpClientSecret,
    setTopUpClientSecret,
    dqInsiderInfo,
    creditData,
    getCreditBalanceService,
    setCreditData,
    setLoading,
    onTopUpSuccess,
    // Opened from a shortfall (the program builder): the line saying what is missing.
    shortfallText = '',
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px' } }}
        >
            <DialogTitle sx={{
                borderBottom: '2px solid #FF6900', pb: 2,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>Top Up Credits</Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>1 Credit = $1 USD · Minimum top-up: $10</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 4 }}>
                {topUpClientSecret ? (
                    <CreditTopUpForm
                        amount={topUpAmount}
                        dqInsiderInfo={dqInsiderInfo}
                        onPaymentSuccess={async () => {
                            setTopUpClientSecret(null);
                            onClose();
                            setLoading(true);
                            let retries = 0;
                            const initialBalance = creditData?.balance_credits || 0;
                            const poll = setInterval(async () => {
                                try {
                                    const resp = await getCreditBalanceService();
                                    const balanceUpdated = resp?.data?.balance_credits > initialBalance;
                                    if (balanceUpdated || retries >= 5) {
                                        setCreditData(resp.data);
                                        setLoading(false);
                                        clearInterval(poll);
                                        if (balanceUpdated && onTopUpSuccess) {
                                            try { onTopUpSuccess(); } catch (_) {}
                                        }
                                    }
                                } catch (e) {
                                    console.error('Polling error:', e);
                                }
                                retries++;
                            }, 1500);
                        }}
                    />
                ) : (
                    <Box>
                        {shortfallText && (
                            <Box sx={{ mt: 2, p: '10px 14px', borderRadius: '8px', background: '#FFF3E0', border: '1px solid #FFCC80' }}>
                                <Typography variant="body2" sx={{ color: '#7A3B00', fontWeight: 600 }}>
                                    {shortfallText} The amount below covers it.
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" sx={{ mb: 1, mt: 2, fontWeight: 600, color: '#64748b' }}>
                            Amount (USD)
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={topUpAmount}
                            onChange={(e) => setTopUpAmount(Math.max(0, parseInt(e.target.value, 10) || 0))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                style: { borderRadius: '8px', fontSize: '1.5rem', fontWeight: 700 },
                            }}
                            sx={{
                                mb: 2.5,
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: '#FF6900' },
                                    '&.Mui-focused fieldset': { borderColor: '#FF6900' },
                                },
                            }}
                        />

                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#64748b' }}>
                            Quick Select
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 3 }}>
                            {[50, 100, 200, 500, 1000].map((amt) => (
                                <Button
                                    key={amt}
                                    variant={topUpAmount === amt ? 'contained' : 'outlined'}
                                    size="small"
                                    onClick={() => setTopUpAmount(amt)}
                                    sx={{
                                        borderRadius: '8px', fontWeight: 700, textTransform: 'none',
                                        ...(topUpAmount === amt ? {
                                            backgroundColor: '#FF6900', borderColor: '#FF6900', color: '#fff',
                                            '&:hover': { backgroundColor: '#e55e00', borderColor: '#e55e00' },
                                        } : {
                                            borderColor: '#e2e8f0', color: '#64748b',
                                            '&:hover': { borderColor: '#FF6900', color: '#FF6900', backgroundColor: '#fff5f5' },
                                        }),
                                    }}
                                >
                                    ${amt}
                                </Button>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                disabled={topUpAmount < 10}
                                onClick={() => setTopUpClientSecret('initiating')}
                                sx={{
                                    backgroundColor: '#FF6900', color: '#fff',
                                    px: 4, py: 1.5, fontWeight: 700, borderRadius: '10px',
                                    textTransform: 'none', fontSize: '1rem', minWidth: '280px',
                                    boxShadow: '0 4px 12px rgba(255,100,104,0.25)',
                                    '&:hover': { backgroundColor: '#e55e00', boxShadow: '0 6px 16px rgba(255,100,104,0.35)' },
                                }}
                            >
                                Continue to Payment
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TopUpCreditsDialog;
